// The list editor for a quick-access bar: the pages on it, in order, with
// move up / move down / remove, plus the pages that can be added, grouped like
// the sidebar.
//
// Feedback #835 (James, 2026-09-30).  Shared by the person's own "Customize"
// dialog (QuickBarCustomizeModal) and the admin's firm default (Settings →
// Features), so the two cannot disagree about ordering, the 8-page limit, or
// which pages are offered.
//
// Controlled: `value` is an array of page keys, `onChange` receives the next
// array.  Saving is the caller's job — this component never calls the API.
//
// `user` + `isFeatureOn` decide what is OFFERED (only pages the editing
// person can open).  A key already in `value` that this person cannot open is
// still listed, flagged with `unavailableNote`, so it can be seen and removed
// rather than silently dropped on the next save.  Both callers rely on that:
// the firm-default editor when a page's feature has been turned off since the
// default was set, and a person's own Customize dialog when a page on their
// bar is switched off or their access to it is taken away.

import {
  QUICK_BAR_MAX, quickBarItemFor, pickableQuickBarGroups,
} from '../lib/quickBarItems'
import { canOpenNavItem } from '../lib/navItems'

const groupLabel = (label) => label.charAt(0) + label.slice(1).toLowerCase()

const iconBtn = 'w-7 h-7 inline-flex items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white'

export default function QuickBarEditor ({
  value, onChange, user, isFeatureOn, disabled = false, unavailableNote = "You can't open this page, so it won't show for you",
}) {
  const keys = Array.isArray(value) ? value : []
  const full = keys.length >= QUICK_BAR_MAX
  const groups = pickableQuickBarGroups(user, isFeatureOn, keys)

  const move = (from, to) => {
    if (to < 0 || to >= keys.length) return
    const next = keys.slice()
    const [k] = next.splice(from, 1)
    next.splice(to, 0, k)
    onChange(next)
  }
  const remove = (key) => onChange(keys.filter((k) => k !== key))
  const add = (key) => {
    if (full || keys.includes(key)) return
    onChange([...keys, key])
  }

  return (
    <div className="space-y-4">
      <section aria-label="Pages on the bar">
        <div className="flex items-baseline justify-between mb-1.5">
          <h4 className="text-sm font-semibold text-gray-800">On the bar (top = leftmost)</h4>
          <span className={`text-xs ${full ? 'text-amber-700 font-semibold' : 'text-gray-500'}`}
            data-testid="quick-bar-count">
            {keys.length} of {QUICK_BAR_MAX}
          </span>
        </div>
        {keys.length === 0 ? (
          <p className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg px-3 py-3">
            No pages — the bar will be empty.  Add pages from the list below.
          </p>
        ) : (
          <ol className="border border-gray-200 rounded-lg divide-y divide-gray-100">
            {keys.map((key, i) => {
              const item = quickBarItemFor(key)
              if (!item) return null
              const usable = canOpenNavItem(user, item, isFeatureOn)
              return (
                <li key={key} className="flex items-center gap-2 px-3 py-1.5" data-testid={`quick-bar-row-${key}`}>
                  <span aria-hidden="true" className="w-6 text-center">{item.icon}</span>
                  <span className="flex-1 min-w-0">
                    <span className={`block text-sm truncate ${usable ? 'text-gray-800' : 'text-gray-400 line-through'}`}>{item.label}</span>
                    {!usable && <span className="block text-[11px] text-amber-700">{unavailableNote}</span>}
                  </span>
                  <button type="button" className={iconBtn} disabled={disabled || i === 0}
                    onClick={() => move(i, i - 1)} aria-label={`Move ${item.label} up`} title="Move up (further left on the bar)">↑</button>
                  <button type="button" className={iconBtn} disabled={disabled || i === keys.length - 1}
                    onClick={() => move(i, i + 1)} aria-label={`Move ${item.label} down`} title="Move down (further right on the bar)">↓</button>
                  <button type="button" className={`${iconBtn} hover:text-red-600`} disabled={disabled}
                    onClick={() => remove(key)} aria-label={`Remove ${item.label}`} title="Remove">✕</button>
                </li>
              )
            })}
          </ol>
        )}
      </section>

      <section aria-label="Pages you can add">
        <h4 className="text-sm font-semibold text-gray-800 mb-1">Add a page</h4>
        {full && (
          <p role="status" className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 mb-2">
            The bar holds at most {QUICK_BAR_MAX} pages.  Remove one to add another.
          </p>
        )}
        {groups.length === 0 ? (
          <p className="text-sm text-gray-500">Every page you can open is already on the bar.</p>
        ) : (
          <div className="space-y-2.5">
            {groups.map((g) => (
              <div key={g.label}>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">{groupLabel(g.label)}</div>
                <div className="flex flex-wrap gap-1.5">
                  {g.items.map((item) => (
                    <button key={item.key} type="button" disabled={disabled || full}
                      onClick={() => add(item.key)}
                      aria-label={`Add ${item.label}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-gray-200 bg-white text-xs text-gray-700 hover:bg-primary-50 hover:border-primary-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200">
                      <span aria-hidden="true">＋</span>
                      <span aria-hidden="true">{item.icon}</span>
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
